import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Grid, Paper, CircularProgress,
    useTheme, IconButton, Tooltip, Chip,
} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    ErrorOutline as CriticalIcon,
    Warning as HighIcon,
    Refresh as RefreshIcon,
    TrendingUp as TrendUpIcon,
    TrendingDown as TrendDownIcon,
    TrendingFlat as TrendFlatIcon,
} from '@mui/icons-material';
import { RootState, Ticket, TicketPriority, TicketStatus } from '../../types';
import { fetchTickets } from '../../store/slices/ticketsSlice';
import { AppDispatch } from '../../store';
import { colorUtils } from '../../theme';
import { getRelativeTimeString } from '../../utils/dateUtils';
import { getPriorityColor, getStatusChipSx, getStatusColor } from '../../theme/mappers/ticketMappers';

// ── Trend ──────────────────────────────────────────────────────────────────────

interface TrendInfo {
    delta: number;
    label: string;
    positiveIsGood: boolean; // true = more is better (e.g. resolved), false = fewer is better (e.g. open)
}

// ── StatCard ───────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
    label: string;
    value: string | number;
    sub?: string;
    icon: React.ElementType;
    color: string;
    trend?: TrendInfo;
}> = ({ label, value, sub, icon: Icon, color, trend }) => {
    const theme = useTheme();

    let trendColor = theme.palette.text.disabled;
    let TrendIcon: React.ElementType = TrendFlatIcon;

    if (trend) {
        if (trend.delta === 0) {
            trendColor = theme.palette.text.disabled;
            TrendIcon = TrendFlatIcon;
        } else {
            const isGood = trend.delta > 0 ? trend.positiveIsGood : !trend.positiveIsGood;
            trendColor = isGood ? theme.palette.severity.low : theme.palette.severity.high;
            TrendIcon = trend.delta > 0 ? TrendUpIcon : TrendDownIcon;
        }
    }

    return (
        <Paper elevation={0} sx={{
            p: 3, borderRadius: 2,
            border: `1px solid ${theme.palette.border.light}`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex', alignItems: 'flex-start', gap: 2,
            height: '100%',
        }}>
            <Box sx={{
                width: 44, height: 44, borderRadius: 2, flexShrink: 0,
                backgroundColor: colorUtils.withOpacity(color, 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Icon sx={{ color, fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.1 }}>
                    {value}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, mt: 0.25 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary, mt: 0.25 }}>
                        {sub}
                    </Typography>
                )}
                {trend && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.75 }}>
                        <TrendIcon sx={{ fontSize: 13, color: trendColor }} />
                        <Typography sx={{ fontSize: '0.68rem', color: trendColor, fontWeight: 600 }}>
                            {trend.delta > 0 ? '+' : ''}{trend.delta} {trend.label}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

// ── Priority bar ───────────────────────────────────────────────────────────────

const SeverityBar: React.FC<{ label: string; count: number; total: number; color: string }> = ({
    label, count, total, color,
}) => {
    const theme = useTheme();
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, textTransform: 'capitalize' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.text.primary }}>
                    {count}
                </Typography>
            </Box>
            <Box sx={{ height: 4, borderRadius: 2, backgroundColor: theme.palette.background.inset, overflow: 'hidden' }}>
                <Box sx={{
                    height: '100%', width: `${pct}%`, borderRadius: 2,
                    backgroundColor: color, transition: 'width 0.6s ease',
                }} />
            </Box>
        </Box>
    );
};

// ── Recent ticket row ──────────────────────────────────────────────────────────

const RecentTicketRow: React.FC<{ ticket: Ticket }> = ({ ticket }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const priorityColor = getPriorityColor(ticket.priority, theme);

    return (
        <Box
            onClick={() => navigate(`/client/tickets/${ticket.id}`)}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.25, borderRadius: 1,
                cursor: 'pointer', transition: 'background 0.15s',
                '&:hover': { backgroundColor: theme.palette.interactive.hover },
            }}
        >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: priorityColor, flexShrink: 0 }} />
            <Typography noWrap sx={{ flex: 1, fontSize: '0.8rem', color: theme.palette.text.primary, fontWeight: 500 }}>
                {ticket.title}
            </Typography>
            <Chip
                label={ticket.status.replace(/_/g, ' ')}
                size="small"
                sx={{ ...getStatusChipSx(getStatusColor(ticket.status, theme)), fontSize: '0.62rem', height: 18, flexShrink: 0 }}
            />
            <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.tertiary, flexShrink: 0, minWidth: 56, textAlign: 'right' }}>
                {getRelativeTimeString(ticket.updated_at)}
            </Typography>
        </Box>
    );
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const generateDueDate = (createdAt: string, priority: TicketPriority): string => {
    const slaHours: Record<TicketPriority, number> = {
        [TicketPriority.CRITICAL]: 4,
        [TicketPriority.HIGH]: 24,
        [TicketPriority.MEDIUM]: 72,
        [TicketPriority.LOW]: 168,
    };
    const due = new Date(createdAt);
    due.setHours(due.getHours() + slaHours[priority]);
    return due.toISOString();
};

// ── Page ───────────────────────────────────────────────────────────────────────

const ClientDashboardPage: React.FC = () => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const { tickets, isLoading } = useSelector((state: RootState) => state.tickets);
    const [lastFetched, setLastFetched] = useState<Date | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const doFetch = async () => {
        await dispatch(fetchTickets({}));
        setLastFetched(new Date());
    };

    useEffect(() => {
        doFetch();
    }, [dispatch]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await doFetch();
        setIsRefreshing(false);
    };

    const stats = useMemo(() => {
        const now = new Date();
        const open = tickets.filter(t => t.status !== TicketStatus.RESOLVED && t.status !== TicketStatus.CLOSED);
        const closed = tickets.filter(t => t.status === TicketStatus.RESOLVED || t.status === TicketStatus.CLOSED);

        // Month helpers
        const thisMonth = now.getMonth();
        const thisYear = now.getFullYear();
        const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
        const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

        const resolvedThisMonth = closed.filter(t => {
            const d = new Date(t.resolved_at || t.updated_at);
            return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
        });
        const resolvedLastMonth = closed.filter(t => {
            const d = new Date(t.resolved_at || t.updated_at);
            return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
        });

        // Week helpers — used as a proxy trend for open incidents
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
        const openedThisWeek = tickets.filter(t => new Date(t.created_at) >= sevenDaysAgo).length;
        const openedLastWeek = tickets.filter(t => {
            const d = new Date(t.created_at);
            return d >= fourteenDaysAgo && d < sevenDaysAgo;
        }).length;

        // Open by priority
        const byPriority = (p: TicketPriority) => open.filter(t => t.priority === p).length;

        // SLA compliance
        const closedWithDue = closed.filter(t => t.due_date || t.created_at);
        const withinSla = closedWithDue.filter(t => {
            const due = new Date(t.due_date || generateDueDate(t.created_at, t.priority));
            const resolved = new Date(t.resolved_at || t.updated_at);
            return resolved <= due;
        });
        const slaCompliance = closedWithDue.length > 0
            ? Math.round((withinSla.length / closedWithDue.length) * 100)
            : null;

        // 5 most recently updated tickets for the recent list
        const recentTickets = [...tickets]
            .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
            .slice(0, 5);

        return {
            totalOpen: open.length,
            critical: byPriority(TicketPriority.CRITICAL),
            high: byPriority(TicketPriority.HIGH),
            medium: byPriority(TicketPriority.MEDIUM),
            low: byPriority(TicketPriority.LOW),
            resolvedThisMonth: resolvedThisMonth.length,
            resolvedLastMonth: resolvedLastMonth.length,
            openedThisWeek,
            openedLastWeek,
            slaCompliance,
            totalClosed: closed.length,
            recentTickets,
        };
    }, [tickets]);

    if (isLoading && tickets.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    const resolvedDelta = stats.resolvedThisMonth - stats.resolvedLastMonth;
    const openedDelta = stats.openedThisWeek - stats.openedLastWeek;

    return (
        <Box sx={{ minHeight: '100vh', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>

            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                    <Typography variant="h1" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                        Overview
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                        Your security posture at a glance
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexShrink: 0 }}>
                    {lastFetched && (
                        <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary }}>
                            Updated {getRelativeTimeString(lastFetched.toISOString())}
                        </Typography>
                    )}
                    <Tooltip title="Refresh data">
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleRefresh}
                                disabled={isRefreshing || isLoading}
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                {isRefreshing
                                    ? <CircularProgress size={16} />
                                    : <RefreshIcon sx={{ fontSize: 18 }} />
                                }
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* KPI cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard
                        label="Open incidents"
                        value={stats.totalOpen}
                        icon={AssignmentIcon}
                        color={theme.palette.severity.high}
                        trend={{ delta: openedDelta, label: 'new vs last week', positiveIsGood: false }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard
                        label="Critical open"
                        value={stats.critical}
                        sub={`${stats.high} high`}
                        icon={CriticalIcon}
                        color={theme.palette.severity.critical}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard
                        label="Resolved this month"
                        value={stats.resolvedThisMonth}
                        sub={`${stats.totalClosed} total closed`}
                        icon={CheckCircleIcon}
                        color={theme.palette.severity.low}
                        trend={{ delta: resolvedDelta, label: 'vs last month', positiveIsGood: true }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard
                        label="SLA compliance"
                        value={stats.slaCompliance !== null ? `${stats.slaCompliance}%` : '—'}
                        sub="based on closed incidents"
                        icon={HighIcon}
                        color={
                            stats.slaCompliance === null ? theme.palette.text.secondary
                            : stats.slaCompliance >= 90 ? theme.palette.severity.low
                            : stats.slaCompliance >= 70 ? theme.palette.severity.medium
                            : theme.palette.severity.critical
                        }
                    />
                </Grid>
            </Grid>

            {/* Bottom row */}
            <Grid container spacing={3}>
                {/* Open by priority / All clear */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Paper elevation={0} sx={{
                        p: 3, borderRadius: 2,
                        border: `1px solid ${theme.palette.border.light}`,
                        backgroundColor: theme.palette.background.paper,
                        height: '100%',
                    }}>
                        <Typography sx={{
                            fontSize: '0.7rem', fontWeight: 700,
                            color: theme.palette.text.tertiary,
                            letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2,
                        }}>
                            Open by priority
                        </Typography>

                        {stats.totalOpen === 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 3 }}>
                                <Box sx={{
                                    width: 52, height: 52, borderRadius: '50%', mb: 1.5,
                                    backgroundColor: colorUtils.withOpacity(theme.palette.severity.low, 0.12),
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <CheckCircleIcon sx={{ fontSize: 26, color: theme.palette.severity.low }} />
                                </Box>
                                <Typography sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
                                    All clear
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                                    No open incidents — your environment looks clean.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <SeverityBar label="Critical" count={stats.critical} total={stats.totalOpen} color={theme.palette.severity.critical} />
                                <SeverityBar label="High"     count={stats.high}     total={stats.totalOpen} color={theme.palette.severity.high} />
                                <SeverityBar label="Medium"   count={stats.medium}   total={stats.totalOpen} color={theme.palette.severity.medium} />
                                <SeverityBar label="Low"      count={stats.low}       total={stats.totalOpen} color={theme.palette.severity.low} />
                            </>
                        )}
                    </Paper>
                </Grid>

                {/* Recent incidents */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Paper elevation={0} sx={{
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.border.light}`,
                        backgroundColor: theme.palette.background.paper,
                        height: '100%',
                        overflow: 'hidden',
                    }}>
                        <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
                            <Typography sx={{
                                fontSize: '0.7rem', fontWeight: 700,
                                color: theme.palette.text.tertiary,
                                letterSpacing: '0.08em', textTransform: 'uppercase',
                            }}>
                                Recent incidents
                            </Typography>
                        </Box>

                        {stats.recentTickets.length === 0 ? (
                            <Box sx={{ px: 3, pb: 3, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.tertiary }}>
                                    No incidents yet
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ px: 1.5, pb: 1.5 }}>
                                {stats.recentTickets.map(t => (
                                    <RecentTicketRow key={t.id} ticket={t} />
                                ))}
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ClientDashboardPage;
